import { FormGroup, FormLabel } from "@eworkit/iris";
import { Formik, FormikHelpers, FormikProps } from "formik";
import React, { FC, memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { FormikError, VALIDATION_TYPES } from "@/components/Formik";
import { ILocation } from "@/data/locations";
import { FormikForm } from "@/formikComponents";
import { FormikTypeaheadLocationHints } from "@/views/components/Formik";
import {
  IProfileFormikProps,
  profilePartialUpdaterBuilder,
} from "@/views/ConsultantProfiles/Detail/profilePartialUpdaterBuilder";

import { Buttons } from "./Buttons";

interface ILocationFormValue {
  location: ILocation | null;
}

const Form: FC<IProfileFormikProps<ILocationFormValue>> = memo(
  ({ initialValues, validationSchema: vs, onSubmit, onCancel, source }) => {
    const { t } = useTranslation("session");

    const isPrivateProfile = !source.companyRole;

    const locationSchema = Yup.object().shape({
      location: Yup.object().nullable(true).required(VALIDATION_TYPES.REQUIRED),
    });

    const finalValidationSchema = !isPrivateProfile ? vs : vs.concat(locationSchema);

    const handleSubmit = useCallback(
      (values: ILocationFormValue, actions: FormikHelpers<ILocationFormValue>) => {
        onSubmit(values, actions);
      },
      [onSubmit],
    );

    return (
      <Formik initialValues={initialValues} validationSchema={finalValidationSchema} onSubmit={handleSubmit}>
        {(form: FormikProps<ILocationFormValue>) => (
          <>
            <FormikForm>
              <FormLabel label={t("CONSULTANT_PROFILES.DETAIL.LOCATION.LABEL")} required={isPrivateProfile} />
              <FormGroup error={<FormikError name="location" />}>
                <FormikTypeaheadLocationHints
                  name="location"
                  testName="location"
                  value={form.values.location}
                  valueKey="name"
                  labelKey="name"
                />
              </FormGroup>
              <Buttons form={form} onClose={onCancel} />
            </FormikForm>
          </>
        )}
      </Formik>
    );
  },
);

export const LocationFormik = profilePartialUpdaterBuilder(Form, {
  validationSchema: Yup.object().shape({}),
  messengerContext: "",
});
